import React, { useState, useEffect } from 'react';
import {
  Grid, Typography, Box, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, Button, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Tabs, Tab,
  Chip, IconButton
} from '@mui/material';
import {
  Add, CheckCircleOutline, TrendingUp, Inventory, LocalShipping,
  Assignment, Build, Error as ErrorIcon, Edit, Delete
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { Kpi, Panel } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import StockInitialDialog from './StockInitialDialog';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

dayjs.extend(isSameOrBefore);

const Stock = () => {
  const { isAdmin, hasRole } = useAuth();
  const [initialOpen, setInitialOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [stockData, setStockData] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [mouvements, setMouvements] = useState(() => {
    const saved = localStorage.getItem('stock_mouvements');
    return saved ? JSON.parse(saved) : [];
  });
  const [lotsProduction, setLotsProduction] = useState(() => {
    const saved = localStorage.getItem('stock_lots');
    return saved ? JSON.parse(saved) : [];
  });
  const [pbaTypes, setPbaTypes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mouvementForm, setMouvementForm] = useState({
    type_operation: 'SORTIE_INTERNE',
    pba_type_id: '',
    quantite: '',
    commentaire: ''
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  useEffect(() => {
    loadPbaTypes();
    loadStockData();
    loadHistorique();
  }, [selectedDate]);

  const loadStockData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/stock/dashboard-quotidien?date=${selectedDate}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStockData(data);
      }
    } catch (err) {
      setStockData([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPbaTypes = async () => {
    try {
      // Utiliser la même API que les autres pages
      const response = await fetch('/api/pba-types', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Types PBA reçus:', data);
        setPbaTypes(data);
      } else {
        console.error('Erreur réponse:', response.status);
        // Fallback avec données statiques
        const pbaTypesData = [
          { id: 1, code: '9AR150', description: 'Poteau béton armé 9m AR150' },
          { id: 2, code: '9AR300', description: 'Poteau béton armé 9m AR300' },
          { id: 3, code: '9AR400', description: 'Poteau béton armé 9m AR400' },
          { id: 4, code: '9AR650', description: 'Poteau béton armé 9m AR650' },
          { id: 5, code: '12AR400', description: 'Poteau béton armé 12m AR400' },
          { id: 6, code: '12AR650', description: 'Poteau béton armé 12m AR650' },
          { id: 8, code: '12B1250', description: 'Poteau béton armé 12m B1250' },
          { id: 9, code: '12B1600', description: 'Poteau béton armé 12m B1600' },
          { id: 10, code: '12B2000', description: 'Poteau béton armé 12m B2000' },
          { id: 11, code: '10B2000', description: 'Poteau béton armé 10m B2000' }
        ];
        setPbaTypes(pbaTypesData);
      }
    } catch (err) {
      console.error('Erreur types PBA:', err);
      // Fallback avec données statiques
      const pbaTypesData = [
        { id: 1, code: '9AR150', description: 'Poteau béton armé 9m AR150' },
        { id: 2, code: '9AR300', description: 'Poteau béton armé 9m AR300' },
        { id: 3, code: '9AR400', description: 'Poteau béton armé 9m AR400' },
        { id: 4, code: '9AR650', description: 'Poteau béton armé 9m AR650' },
        { id: 5, code: '12AR400', description: 'Poteau béton armé 12m AR400' },
        { id: 6, code: '12AR650', description: 'Poteau béton armé 12m AR650' },
        { id: 8, code: '12B1250', description: 'Poteau béton armé 12m B1250' },
        { id: 9, code: '12B1600', description: 'Poteau béton armé 12m B1600' },
        { id: 10, code: '12B2000', description: 'Poteau béton armé 12m B2000' },
        { id: 11, code: '10B2000', description: 'Poteau béton armé 10m B2000' }
      ];
      setPbaTypes(pbaTypesData);
    }
  };

  const loadHistorique = async () => {
    try {
      const response = await fetch(`/api/stock/historique?date=${selectedDate}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Gérer la nouvelle structure de réponse
        if (data.movements) {
          setHistorique(data.movements);
        } else if (Array.isArray(data)) {
          setHistorique(data);
        } else {
          setHistorique([]);
        }
      }
    } catch (err) {
      setHistorique([]);
    }
  };

  const handleSubmitMouvement = async () => {
    try {
      const response = await fetch('/api/stock/mouvement', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          date: selectedDate,
          type_operation: mouvementForm.type_operation,
          pba_type_id: mouvementForm.pba_type_id,
          quantite: parseInt(mouvementForm.quantite),
          commentaire: mouvementForm.commentaire
        })
      });
      
      if (response.ok) {
        setDialogOpen(false);
        setMouvementForm({ type_operation: 'SORTIE_INTERNE', pba_type_id: '', quantite: '', commentaire: '' });
        await loadStockData();
        await loadHistorique();
        showAlert('Mouvement enregistré avec succès', 'success');
      } else {
        const error = await response.json();
        showAlert(error.error || 'Erreur lors de l\'enregistrement', 'error');
      }
    } catch (err) {
      console.error('Erreur:', err);
      showAlert('Erreur de connexion au serveur', 'error');
    }
  };
  



  
  const restaurerLots = (lots, pbaCode, quantiteARestaurer) => {
    const lotsModifies = [...lots];
    let quantiteRestante = quantiteARestaurer;
    
    // Restaurer dans les lots matures (ordre inverse du FIFO)
    const lotsMatures = lotsModifies
      .filter(lot => lot.pba_code === pbaCode && lot.est_mature)
      .sort((a, b) => new Date(b.date_production) - new Date(a.date_production));
    
    for (let lot of lotsMatures) {
      if (quantiteRestante <= 0) break;
      
      const quantiteARestaurer = Math.min(lot.quantite_initiale - lot.quantite_restante, quantiteRestante);
      lot.quantite_restante += quantiteARestaurer;
      quantiteRestante -= quantiteARestaurer;
    }
    
    return lotsModifies;
  };
  
  const deduireDesLotsMatures = (lots, pbaCode, quantiteADeduire) => {
    const lotsModifies = [...lots];
    let quantiteRestante = quantiteADeduire;
    
    // Trier les lots matures par date de production (FIFO)
    const lotsMatures = lotsModifies
      .filter(lot => lot.pba_code === pbaCode && lot.est_mature && lot.quantite_restante > 0)
      .sort((a, b) => new Date(a.date_production) - new Date(b.date_production));
    
    for (let lot of lotsMatures) {
      if (quantiteRestante <= 0) break;
      
      const quantiteAPrendre = Math.min(lot.quantite_restante, quantiteRestante);
      lot.quantite_restante -= quantiteAPrendre;
      quantiteRestante -= quantiteAPrendre;
    }
    
    return lotsModifies;
  };
  
  const calculerStockPourPBA = (pbaCode, date) => {
    const mouvementsJusquADate = mouvements.filter(m => 
      m.pba_code === pbaCode && (dayjs(date).isAfter(dayjs(m.date)) || dayjs(date).isSame(dayjs(m.date)))
    );
    
    let stock_total = 0;
    let stock_disponible = 0;
    
    // Calculer stock total
    mouvementsJusquADate.forEach(mouvement => {
      if (mouvement.type_operation === 'PRODUCTION') {
        stock_total += mouvement.quantite;
      } else if (['LIVRAISON', 'SORTIE_INTERNE', 'AVARIE'].includes(mouvement.type_operation)) {
        stock_total -= mouvement.quantite;
      }
    });
    
    // Calculer stock disponible (lots matures)
    const lotsPourPBA = lotsProduction.filter(lot => lot.pba_code === pbaCode);
    stock_disponible = lotsPourPBA
      .filter(lot => dayjs(date).isAfter(dayjs(lot.date_maturation)) || dayjs(date).isSame(dayjs(lot.date_maturation)))
      .reduce((sum, lot) => sum + lot.quantite_restante, 0);
    
    return { stock_total, stock_disponible };
  };
  
  const calculerStockDynamique = (mouvementsList, lotsList = lotsProduction) => {
    const stockCalcule = {};
    
    // Initialiser tous les PBA
    pbaTypes.forEach(pba => {
      const dateHier = dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD');
      const stockHier = calculerStockPourPBAAvecLots(pba.code, dateHier, mouvementsList, lotsList);
      
      stockCalcule[pba.code] = {
        code: pba.code,
        stock_initial: stockHier.stock_total,
        production_jour: 0,
        livraison_jour: 0,
        sortie_interne_jour: 0,
        avarie_jour: 0,
        stock_total: stockHier.stock_total,
        stock_disponible: stockHier.stock_disponible
      };
    });
    
    // Calculer les mouvements du jour sélectionné
    mouvementsList
      .filter(m => m.date === selectedDate)
      .forEach(mouvement => {
        const stock = stockCalcule[mouvement.pba_code];
        if (stock) {
          switch (mouvement.type_operation) {
            case 'PRODUCTION':
              stock.production_jour += mouvement.quantite;
              stock.stock_total += mouvement.quantite;
              // Production disponible après la durée de maturation (Paramètres → Production)
              const dateMaturation = dayjs(selectedDate).add(10, 'days');
              if (dayjs(selectedDate).isAfter(dateMaturation) || dayjs(selectedDate).isSame(dateMaturation)) {
                stock.stock_disponible += mouvement.quantite;
              }
              break;
            case 'LIVRAISON':
              stock.livraison_jour += mouvement.quantite;
              stock.stock_total -= mouvement.quantite;
              stock.stock_disponible -= mouvement.quantite;
              break;
            case 'SORTIE_INTERNE':
              stock.sortie_interne_jour += mouvement.quantite;
              stock.stock_total -= mouvement.quantite;
              stock.stock_disponible -= mouvement.quantite;
              break;
            case 'AVARIE':
              stock.avarie_jour += mouvement.quantite;
              stock.stock_total -= mouvement.quantite;
              stock.stock_disponible -= mouvement.quantite;
              break;
          }
        }
      });
    
    // Recalculer le stock disponible basé sur les lots matures
    pbaTypes.forEach(pba => {
      const stock = stockCalcule[pba.code];
      const stockCalculeAvecLots = calculerStockPourPBAAvecLots(pba.code, selectedDate, mouvementsList, lotsList);
      stock.stock_disponible = stockCalculeAvecLots.stock_disponible;
    });
    
    // Convertir en array et inclure tous les PBA avec stock > 0 ou mouvements du jour
    const stockArray = Object.values(stockCalcule).filter(stock => 
      stock.stock_total > 0 || stock.production_jour > 0 || stock.livraison_jour > 0 || 
      stock.sortie_interne_jour > 0 || stock.avarie_jour > 0
    );
    
    setStockData(stockArray);
  };
  
  const calculerStockPourPBAAvecLots = (pbaCode, date, mouvementsList, lotsList) => {
    const mouvementsJusquADate = mouvementsList.filter(m => 
      m.pba_code === pbaCode && (dayjs(date).isAfter(dayjs(m.date)) || dayjs(date).isSame(dayjs(m.date)))
    );
    
    let stock_total = 0;
    
    // Calculer stock total
    mouvementsJusquADate.forEach(mouvement => {
      if (mouvement.type_operation === 'PRODUCTION') {
        stock_total += mouvement.quantite;
      } else if (['LIVRAISON', 'SORTIE_INTERNE', 'AVARIE'].includes(mouvement.type_operation)) {
        stock_total -= mouvement.quantite;
      }
    });
    
    // Calculer stock disponible (lots matures)
    const lotsPourPBA = lotsList.filter(lot => lot.pba_code === pbaCode);
    const stock_disponible = lotsPourPBA
      .filter(lot => dayjs(date).isAfter(dayjs(lot.date_maturation)) || dayjs(date).isSame(dayjs(lot.date_maturation)))
      .reduce((sum, lot) => sum + lot.quantite_restante, 0);
    
    return { stock_total, stock_disponible };
  };

  const showAlert = (message, severity) => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 3000);
  };

  const getOperationIcon = (type) => {
    switch (type) {
      case 'PRODUCTION': return <TrendingUp color="primary" />;
      case 'LIVRAISON': return <LocalShipping color="warning" />;
      case 'SORTIE_INTERNE': return <Assignment color="info" />;
      case 'AVARIE': return <ErrorIcon color="error" />;
      case 'MATURATION_AUTO': return <Build color="success" />;
      case 'AJUSTEMENT_MANUEL': return <Build color="secondary" />;
      default: return <Inventory />;
    }
  };

  const getOperationColor = (type) => {
    switch (type) {
      case 'PRODUCTION': return 'primary';
      case 'LIVRAISON': return 'warning';
      case 'SORTIE_INTERNE': return 'info';
      case 'AVARIE': return 'error';
      case 'MATURATION_AUTO': return 'success';
      case 'AJUSTEMENT_MANUEL': return 'secondary';
      default: return 'default';
    }
  };

  if (loading) {
    return <Layout title="Stock PBA"><Typography>Chargement...</Typography></Layout>;
  }

  const totalStockTotal = stockData.reduce((sum, item) => sum + parseInt(item.stock_total || 0), 0);
  const totalStockDisponible = stockData.reduce((sum, item) => sum + parseInt(item.stock_disponible || 0), 0);
  const totalProduction = stockData.reduce((sum, item) => sum + parseInt(item.production_jour || 0), 0);
  const totalLivraison = stockData.reduce((sum, item) => sum + parseInt(item.livraison_jour || 0), 0);

  return (
    <Layout title="Stock PBA" plain>
      {alert.show && <Alert severity={alert.severity} sx={{ mb: 2 }}>{alert.message}</Alert>}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <TextField type="date" size="small" label="Date" value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {(isAdmin || hasRole('sous_admin')) && <Button variant="outlined" startIcon={<Inventory />} onClick={() => setInitialOpen(true)}>Stock initial (reprise)</Button>}
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>Nouveau mouvement</Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}><Kpi icon={Inventory} tint="navy" label="Stock total" value={totalStockTotal} sub="Tous types confondus" /></Grid>
        <Grid item xs={6} md={3}><Kpi icon={CheckCircleOutline} tint="green" label="Stock disponible" value={totalStockDisponible} sub="Maturés, livrables" /></Grid>
        <Grid item xs={6} md={3}><Kpi icon={TrendingUp} tint="navy" label="Production du jour" value={totalProduction} sub={dayjs(selectedDate).format('DD/MM/YYYY')} /></Grid>
        <Grid item xs={6} md={3}><Kpi icon={LocalShipping} tint="amber" label="Livraisons du jour" value={totalLivraison} sub={dayjs(selectedDate).format('DD/MM/YYYY')} /></Grid>
      </Grid>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label="Stock par PBA" />
        <Tab label="Historique des mouvements" />
      </Tabs>

      {/* Tableau Stock par PBA */}
      {tabValue === 0 && (
        <Panel flush><TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code PBA</TableCell>
                <TableCell align="right">Stock Initial</TableCell>
                <TableCell align="right">Production</TableCell>
                <TableCell align="right">Livraisons</TableCell>
                <TableCell align="right">Sorties Int.</TableCell>
                <TableCell align="right">Avaries</TableCell>
                <TableCell align="right">Stock total</TableCell>
                <TableCell align="right">Stock disponible</TableCell>
                <TableCell align="center">Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stockData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      Aucune donnée pour le {dayjs(selectedDate).format('DD/MM/YYYY')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                stockData.map((item) => (
                  <TableRow key={item.code} hover>
                    <TableCell>{item.code}</TableCell>
                    <TableCell align="right">{item.stock_initial || 0}</TableCell>
                    <TableCell align="right" sx={{ color: Number(item.production_jour) ? 'primary.main' : 'text.disabled', fontWeight: 600 }}>
                      {Number(item.production_jour) ? `+${item.production_jour}` : '0'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: Number(item.livraison_jour) ? 'text.primary' : 'text.disabled', fontWeight: 600 }}>
                      {Number(item.livraison_jour) ? `−${item.livraison_jour}` : '0'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: Number(item.sortie_interne_jour) ? 'text.primary' : 'text.disabled', fontWeight: 600 }}>
                      {Number(item.sortie_interne_jour) ? `−${item.sortie_interne_jour}` : '0'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: Number(item.avarie_jour) ? 'error.main' : 'text.disabled', fontWeight: 600 }}>
                      {Number(item.avarie_jour) ? `−${item.avarie_jour}` : '0'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: '"IBM Plex Mono", monospace', fontWeight: 600, fontSize: 16 }}>
                      {item.stock_total || 0}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontFamily: '"IBM Plex Mono", monospace', fontWeight: 600, fontSize: 16,
                      color: Number(item.stock_disponible) === 0 ? 'error.main' : Number(item.stock_disponible) < 10 ? 'warning.main' : 'text.primary',
                    }}>
                      {item.stock_disponible || 0}
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={Number(item.stock_disponible) === 0 ? 'Rupture' : Number(item.stock_disponible) < 10 ? 'Faible' : 'Normal'}
                        color={Number(item.stock_disponible) === 0 ? 'error' : Number(item.stock_disponible) < 10 ? 'warning' : 'success'}
                        variant="outlined"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer></Panel>
      )}

      {/* Historique des mouvements */}
      {tabValue === 1 && (
        <Panel flush><TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date/Heure</TableCell>
                <TableCell>Opérations</TableCell>
                <TableCell>PBA</TableCell>
                <TableCell align="right">Nb Mouvements</TableCell>
                <TableCell>Utilisateurs</TableCell>
                <TableCell>Commentaires</TableCell>
                <TableCell>Statut</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(historique || []).map((mouvement, index) => (
                <TableRow key={index} hover>
                  <TableCell>{dayjs(mouvement.derniere_modification).format('DD/MM/YYYY HH:mm')}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      {mouvement.production_total > 0 && (
                        <Chip label={`Production: ${mouvement.production_total}`} color="primary" size="small" />
                      )}
                      {mouvement.livraison_total > 0 && (
                        <Chip label={`Livraison: ${mouvement.livraison_total}`} color="warning" size="small" />
                      )}
                      {mouvement.sortie_interne_total > 0 && (
                        <Chip label={`Sortie: ${mouvement.sortie_interne_total}`} color="info" size="small" />
                      )}
                      {mouvement.avarie_total > 0 && (
                        <Chip label={`Avarie: ${mouvement.avarie_total}`} color="error" size="small" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{mouvement.pba_code}</TableCell>
                  <TableCell align="right">
                    <Typography fontWeight="bold">
                      {mouvement.nb_mouvements} mouvement(s)
                    </Typography>
                  </TableCell>
                  <TableCell>{mouvement.utilisateurs || 'Système'}</TableCell>
                  <TableCell>Mouvement regroupé</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="textSecondary">
                      Regroupé
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer></Panel>
      )}

      {/* Dialog nouveau mouvement */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau Mouvement - {dayjs(selectedDate).format('DD/MM/YYYY')}</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Type d'opération"
            value={mouvementForm.type_operation}
            onChange={(e) => setMouvementForm({...mouvementForm, type_operation: e.target.value})}
            margin="normal"
          >
            <MenuItem value="SORTIE_INTERNE">Sortie Interne</MenuItem>
            <MenuItem value="AVARIE_MATURE">Avarie (PBA Mature)</MenuItem>
            <MenuItem value="AVARIE_IMMATURE">Avarie (PBA Immature)</MenuItem>
          </TextField>
          
          <TextField
            select
            fullWidth
            label="Type PBA"
            value={mouvementForm.pba_type_id}
            onChange={(e) => setMouvementForm({...mouvementForm, pba_type_id: e.target.value})}
            margin="normal"
            required
          >
            {pbaTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>{type.code} - {type.description}</MenuItem>
            ))}
          </TextField>
          
          <TextField
            fullWidth
            type="number"
            label="Quantité"
            value={mouvementForm.quantite}
            onChange={(e) => setMouvementForm({...mouvementForm, quantite: e.target.value})}
            margin="normal"
            required
            inputProps={{ min: 0 }}
          />
          

          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Commentaire"
            value={mouvementForm.commentaire}
            onChange={(e) => setMouvementForm({...mouvementForm, commentaire: e.target.value})}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Annuler</Button>
          <Button 
            onClick={handleSubmitMouvement} 
            variant="contained"
            disabled={!mouvementForm.pba_type_id || !mouvementForm.quantite}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>


      <StockInitialDialog open={initialOpen} onClose={() => setInitialOpen(false)}
        onSaved={(msg, rester) => { if (!rester) setInitialOpen(false); showAlert(msg, 'success'); loadStockData(); loadHistorique(); }} />
    </Layout>
  );
};

export default Stock;